/* eslint-disable max-lines */
/* eslint-disable no-empty-pattern */
/* eslint-disable max-lines-per-function */
/* eslint-disable camelcase */
import {
  ChangeEvent,
  CSSProperties,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Autocomplete,
  AutocompleteChanges,
  Button,
  Checkbox,
  EdsProvider,
  Icon,
  Typography,
} from '@equinor/eds-core-react';
import { CenteredLoader } from '../../components/CenteredLoader/CenteredLoader';
import {
  ExpandableListCell,
  isDisplayValue,
} from './Components/ExpandableListCell';
import { view_column } from '@equinor/eds-icons';
import {
  EdsDataGrid,
  FilterWrapper,
  HeaderContext,
} from '@equinor/eds-data-grid-react';
import type { VisibilityState } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import {
  AnalogueModelConfigurationDto,
  AnalogueModelList,
  StratigraphicGroupDto,
  StratUnitDto,
} from '../../api/generated';
import { useFetchAnalogueModels } from '../../hooks/useFetchAnalogueModels';
import {
  getArchelDisplayName,
  getDisplayFilterValues,
  isDisplayFilterValue,
} from '../../utils/DisplayNames';
import { getTextColumnWidths } from '../../utils/TableColumnWidth';
import * as Styled from './ModelTable.styled';
import { useExportStore } from '../../stores/ExportStore';
import { useFilterStore } from '../../stores/FilterStore';
import { useShallow } from 'zustand/react/shallow';

export enum ModelStatus {
  UNKNOWN = 'Unknown',

  TRANSFORMING = 'Transforming ...',
  SUCCEEDED = 'Succeeded',
  FAILED_UPLOADING = 'Uploading failed',
  FAILED_TRANSFORMING = 'Transforming failed',
}

const parseModelConfiguration = (
  configuration: AnalogueModelConfigurationDto | undefined,
) => {
  if (!configuration) {
    return {
      template: '-',
      basinSlope: '-',
      composition: '-',
      riverLength: '-',
      simStopTime: '-',
      channelWidth: '-',
      subsidenceSea: '-',
      waveDirection: '-',
      waveHeightFin: '-',
      waveHeightIni: '-',
      outputInterval: '-',
      subsidenceLand: '-',
      tidalAmplitude: '-',
      riverDischargeFin: '-',
      riverDischargeIni: '-',
    };
  }

  return {
    template: configuration.template?.value ?? '-',
    basinSlope: configuration.basinSlope?.value ?? '-',
    composition: configuration.composition?.value ?? '-',
    riverLength: configuration.riverLength?.value ?? '-',
    simStopTime: configuration.simStopTime?.value ?? '-',
    channelWidth: configuration.channelWidth?.value ?? '-',
    subsidenceSea: configuration.subsidenceSea?.value ?? '-',
    waveDirection: configuration.waveDirection?.value ?? '-',
    waveHeightFin: configuration.waveHeightFin?.value ?? '-',
    waveHeightIni: configuration.waveHeightIni?.value ?? '-',
    outputInterval: configuration.outputInterval?.value ?? '-',
    subsidenceLand: configuration.subsidenceLand?.value ?? '-',
    tidalAmplitude: configuration.tidalAmplitude?.value ?? '-',
    riverDischargeFin: configuration.riverDischargeFin?.value ?? '-',
    riverDischargeIni: configuration.riverDischargeIni?.value ?? '-',
  };
};

const modelConfigurationColumnSettings = [
  { header: 'Template', id: 'template', minWidth: 180 },
  { header: 'Basin slope', id: 'basinSlope' },
  { header: 'Sediment classes', id: 'composition', minWidth: 150 },
  { header: 'River length', id: 'riverLength', minWidth: 130 },
  { header: 'Simulation time', id: 'simStopTime', minWidth: 140 },
  { header: 'Channel width', id: 'channelWidth', minWidth: 130 },
  { header: 'Subsidence at seaward', id: 'subsidenceSea', minWidth: 180 },
  { header: 'Wave direction', id: 'waveDirection', minWidth: 130 },
  { header: 'Final wave height', id: 'waveHeightFin', minWidth: 150 },
  { header: 'Initial wave height', id: 'waveHeightIni', minWidth: 150 },
  { header: 'Output interval', id: 'outputInterval', minWidth: 140 },
  { header: 'Subsidence at landward', id: 'subsidenceLand', minWidth: 180 },
  { header: 'Tidal amplitude', id: 'tidalAmplitude', minWidth: 140 },
  { header: 'Final river discharge', id: 'riverDischargeFin', minWidth: 170 },
  { header: 'Initial river discharge', id: 'riverDischargeIni', minWidth: 180 },
] as const;

const getRowGroup = (stratGroupList: StratigraphicGroupDto[]) => {
  const groupList: StratUnitDto[] = [];

  if (stratGroupList.length > 0) {
    stratGroupList.forEach((column) => {
      column.stratUnits
        .filter((item) => item.level === 1)
        .forEach((i) => {
          if (
            groupList.filter((item) => i.stratUnitId === item.stratUnitId)
              .length <= 0
          )
            groupList.push(i);
        });
    });
  }
  return groupList;
};

const getRowStratUnits = (
  stratGroupList: StratigraphicGroupDto[],
  level: number,
) => {
  const unitList: StratUnitDto[] = [];
  if (stratGroupList.length > 0) {
    stratGroupList.forEach((column) => {
      column.stratUnits
        .filter((item) => item.level === level)
        .forEach((i) => {
          if (
            unitList.filter((item) => i.stratUnitId === item.stratUnitId)
              .length <= 0
          )
            unitList.push(i);
        });
    });
  }
  return unitList;
};

export const ModelTable = ({
  toolbarActions,
}: {
  toolbarActions?: React.ReactNode;
}) => {
  const { addExportModel, deleteExportModel, exportModels } = useExportStore(
    useShallow((s) => ({
      addExportModel: s.addExportModel,
      deleteExportModel: s.deleteExportModel,
      exportModels: s.exportModels,
    })),
  );
  const {
    countryFilterList,
    setCountryFilterList,
    outcropFilterList,
    setOutcropFilterList,
    fieldFilterList,
    setFieldFilterList,
    stratColFilterList,
    setStratColFilterList,
    groupFilterList,
    setGroupFilterList,
    gdeFilterList,
    setGdeFilterList,
    deFilterList,
    setDeFilterList,
    seFilterList,
    setSeFilterList,
    aeFilterList,
    setAeFilterList,
    formationFilterList,
    setFormationFilterList,
    subzoneFilterList,
    setSubzoneFilterList,
  } = useFilterStore(
    useShallow((s) => ({
      countryFilterList: s.countryFilterList,
      setCountryFilterList: s.setCountryFilterList,
      outcropFilterList: s.outcropFilterList,
      setOutcropFilterList: s.setOutcropFilterList,
      fieldFilterList: s.fieldFilterList,
      setFieldFilterList: s.setFieldFilterList,
      stratColFilterList: s.stratColFilterList,
      setStratColFilterList: s.setStratColFilterList,
      groupFilterList: s.groupFilterList,
      setGroupFilterList: s.setGroupFilterList,
      gdeFilterList: s.gdeFilterList,
      setGdeFilterList: s.setGdeFilterList,
      deFilterList: s.deFilterList,
      setDeFilterList: s.setDeFilterList,
      seFilterList: s.seFilterList,
      setSeFilterList: s.setSeFilterList,
      aeFilterList: s.aeFilterList,
      setAeFilterList: s.setAeFilterList,
      formationFilterList: s.formationFilterList,
      setFormationFilterList: s.setFormationFilterList,
      subzoneFilterList: s.subzoneFilterList,
      setSubzoneFilterList: s.setSubzoneFilterList,
    })),
  );
  const navigate = useNavigate();
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    name: true,
    template: true,
    basinSlope: true,
    composition: true,
    riverLength: false,
    simStopTime: false,
    channelWidth: false,
    subsidenceSea: false,
    waveDirection: false,
    waveHeightFin: false,
    waveHeightIni: false,
    outputInterval: false,
    subsidenceLand: false,
    tidalAmplitude: false,
    riverDischargeFin: false,
    riverDischargeIni: false,
    outcrops: true,
    country: true,
    field: true,
    stratigraphicColumn: true,
    group: true,
    gde: true,
    de: true,
    se: true,
    ae: true,
    formation: true,
    subzone: true,
    isProcessed: true,
    createdBy: true,
    createdDate: false,
  });
  const [isSideSheetOpen, setIsSideSheetOpen] = useState(false);

  const { isLoading, data } = useFetchAnalogueModels();

  const columnSizes = useMemo(() => {
    const models = data ?? [];
    const parsedConfigurations = models.map((model) =>
      parseModelConfiguration(model.iniParameters),
    );
    const valuesFromModels = (
      getValues: (model: AnalogueModelList) => (string | undefined)[],
    ) => models.flatMap(getValues);

    return getTextColumnWidths([
      {
        header: 'Model name',
        id: 'name',
        minWidth: 200,
        values: models.map((model) => model.name),
      },
      ...modelConfigurationColumnSettings.map(({ id, ...options }) => ({
        ...options,
        id,
        values: parsedConfigurations.map((configuration) => configuration[id]),
      })),
      {
        header: 'Outcrop',
        id: 'outcrops',
        values: valuesFromModels((model) =>
          model.outcrops.map((outcrop) => outcrop.name),
        ),
      },
      {
        header: 'Country',
        id: 'country',
        values: valuesFromModels((model) =>
          model.stratigraphicGroups.map((group) => group.country.identifier),
        ),
      },
      {
        header: 'Field',
        id: 'field',
        values: valuesFromModels((model) =>
          model.stratigraphicGroups.map((group) => group.field.identifier),
        ),
      },
      {
        header: 'Stratigraphic column',
        id: 'stratigraphicColumn',
        minWidth: 230,
        values: valuesFromModels((model) =>
          model.stratigraphicGroups.map(
            (group) => group.stratColumn.identifier,
          ),
        ),
      },
      {
        header: 'Group',
        id: 'group',
        minWidth: 150,
        values: valuesFromModels((model) =>
          getRowGroup(model.stratigraphicGroups).map(
            (group) => group.identifier,
          ),
        ),
      },
      {
        header: 'Formation',
        id: 'formation',
        minWidth: 150,
        values: valuesFromModels((model) =>
          getRowStratUnits(model.stratigraphicGroups, 2).map(
            (unit) => unit.identifier,
          ),
        ),
      },
      {
        header: 'Subzone',
        id: 'subzone',
        minWidth: 150,
        values: valuesFromModels((model) =>
          getRowStratUnits(model.stratigraphicGroups, 3).map(
            (unit) => unit.identifier,
          ),
        ),
      },
      {
        header: 'GDE',
        id: 'gde',
        minWidth: 150,
        values: valuesFromModels((model) =>
          model.geologicalGroups.map((group) => group.grossDepEnv.identifier),
        ),
      },
      {
        header: 'DE',
        id: 'de',
        minWidth: 150,
        values: valuesFromModels((model) =>
          model.geologicalGroups.map((group) => group.depEnv.identifier),
        ),
      },
      {
        header: 'SE',
        id: 'se',
        minWidth: 150,
        values: valuesFromModels((model) =>
          model.geologicalGroups.map((group) => group.subenv.identifier),
        ),
      },
      {
        header: 'AE',
        id: 'ae',
        minWidth: 150,
        values: valuesFromModels((model) =>
          (model.iniParameters?.archels ?? [])
            .map(getArchelDisplayName)
            .filter(isDisplayFilterValue),
        ),
      },
      {
        header: 'Created by',
        id: 'createdBy',
        minWidth: 160,
        values: models.map((model) => model.createdBy),
      },
      {
        header: 'Created date',
        id: 'createdDate',
        minWidth: 160,
        values: models.map((model) =>
          model.createdDate
            ? new Date(model.createdDate).toLocaleDateString()
            : '-',
        ),
      },
      {
        header: 'Status',
        id: 'isProcessed',
        minWidth: 160,
        values: Object.values(ModelStatus),
      },
    ]);
  }, [data]);

  useEffect(() => {
    if (data !== undefined) {
      setCountryFilterList(
        data
          .map((i) => i.stratigraphicGroups.map((s) => s.country.identifier))
          .flat(),
      );
      setOutcropFilterList(
        data.map((i) => i.outcrops.map((i) => i.name)).flat(),
      );
      setFieldFilterList(
        data
          .map((i) => i.stratigraphicGroups.map((s) => s.field.identifier))
          .flat(),
      );
      setStratColFilterList(
        data
          .map((i) =>
            i.stratigraphicGroups.map((s) => s.stratColumn.identifier),
          )
          .flat(),
      );
      setGroupFilterList(
        data
          .map((i) =>
            getRowGroup(i.stratigraphicGroups).map((s) => s.identifier),
          )
          .flat(),
      );
      setGdeFilterList(
        data
          .map((i) => i.geologicalGroups.map((g) => g.grossDepEnv.identifier))
          .flat(),
      );
      setDeFilterList(
        data
          .map((i) => i.geologicalGroups.map((g) => g.depEnv.identifier))
          .flat(),
      );
      setSeFilterList(
        data
          .map((i) => i.geologicalGroups.map((g) => g.subenv.identifier))
          .flat(),
      );
      setAeFilterList(
        data
          .map((i) =>
            (i.iniParameters?.archels ?? [])
              .map(
                (a) =>
                  a.analogueModelComputeSettingArchelMap?.identifier ??
                  a.name ??
                  '',
              )
              .filter(isDisplayValue),
          )
          .flat(),
      );
      setFormationFilterList(
        data
          .map((i) =>
            getRowStratUnits(i.stratigraphicGroups, 2).map((s) => s.identifier),
          )
          .flat(),
      );
      setSubzoneFilterList(
        data
          .map((i) =>
            getRowStratUnits(i.stratigraphicGroups, 3).map((s) => s.identifier),
          )
          .flat(),
      );
    }
  }, [
    data,
    setCountryFilterList,
    setFieldFilterList,
    setGroupFilterList,
    setOutcropFilterList,
    setStratColFilterList,
    setGdeFilterList,
    setDeFilterList,
    setSeFilterList,
    setAeFilterList,
    setFormationFilterList,
    setSubzoneFilterList,
  ]);

  if (isLoading || !data) return <CenteredLoader />;

  const createdByFilterList = data
    .map((m) => m.createdBy)
    .filter((v): v is string => !!v);

  const getModelStatus = (id: string) => {
    let status = ModelStatus.UNKNOWN;

    const model = data.find((m) => m.analogueModelId === id);
    const transforming =
      model && model.processingStatus ? model.processingStatus : undefined;
    const isProcessed = model ? model.isProcessed : undefined;
    const uploadStatus = model?.uploads.map((x) => x.uploadStatus);

    if (isProcessed === true) {
      status = ModelStatus.SUCCEEDED;
    } else if (
      transforming === 'Created' ||
      transforming === 'Waiting' ||
      transforming === 'Running'
    ) {
      status = ModelStatus.TRANSFORMING;
    } else if (transforming === 'Failed') {
      status = ModelStatus.FAILED_TRANSFORMING;
    } else if (uploadStatus?.find((x) => x === 'Failed')) {
      status = ModelStatus.FAILED_UPLOADING;
    }

    return status;
  };

  const addModelToExport = (checked: boolean, modelId: string) => {
    if (checked) addExportModel(modelId);
    else deleteExportModel(modelId);
  };

  const toggleColumn = (columnId: string) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  /* Make sure the header row in EdsDataGrid is vertically middle-aligned when filter icons are shown */
  const headerStyle = (): CSSProperties => ({ verticalAlign: 'middle' });

  const headerComponent = (
    header: HeaderContext<AnalogueModelList, unknown>,
    title: string,
    options: string[],
  ) => {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant={'cell_header'} group={'table'}>
          {title}
        </Typography>
        <FilterWrapper
          column={header.column}
          CustomComponent={({ onChange, value }) =>
            SelectFilterComponent({ onChange, value }, options)
          }
        />
      </div>
    );
  };

  const SelectFilterComponent = (
    {
      onChange,
      value,
    }: {
      onChange: (value: unknown) => void;
      value: unknown;
    },
    options: string[],
  ) => {
    const filterValues = useMemo(
      () => (value as string[] | undefined) ?? [],
      [value],
    );
    // This is always the correct List, but the properties should usually be different - need a new context?
    const [selectedValues, setSelectedValues] = useState(() =>
      getDisplayFilterValues(filterValues),
    );
    const uniqueOptions = Array.from(new Set(getDisplayFilterValues(options)));

    useEffect(() => {
      setSelectedValues(getDisplayFilterValues(filterValues)); // Update selectedValues when value prop changes
    }, [filterValues]);

    const handleSelections = (e: AutocompleteChanges<string>) => {
      const sanitizedSelections = getDisplayFilterValues(e.selectedItems);

      setSelectedValues(sanitizedSelections);
      onChange(sanitizedSelections);
    };

    return (
      <Autocomplete
        label="Select multiple labels"
        options={uniqueOptions}
        multiple
        initialSelectedOptions={selectedValues}
        onOptionsChange={handleSelections}
      />
    );
  };

  const filterFunction = (filterValue: string[], data: string[]) => {
    if (!filterValue || filterValue.length === 0) return true;

    let arr: string[] = [];
    filterValue.forEach((item: string) => {
      if (item === '') return;
      arr = arr.concat(
        data.filter((key: string) =>
          key.toLowerCase().includes(item.toLowerCase()),
        ),
      );
    });

    if (arr && arr.length > 0) return true;
    return false;
  };

  const getRowArchels = (row: AnalogueModelList) =>
    (row.iniParameters?.archels ?? [])
      .map(
        (a) =>
          a.analogueModelComputeSettingArchelMap?.identifier ?? a.name ?? '',
      )
      .filter(isDisplayValue);

  return (
    <Styled.Table>
      <Styled.ColumnsButton>
        {toolbarActions && (
          <Styled.ToolbarActions>{toolbarActions}</Styled.ToolbarActions>
        )}
        <Button
          variant="outlined"
          onClick={() => setIsSideSheetOpen(!isSideSheetOpen)}
          aria-label="Toggle columns"
        >
          Toggle columns&nbsp;
          <Icon data={view_column} />
        </Button>
      </Styled.ColumnsButton>
      <EdsDataGrid
        enableSorting
        enableColumnFiltering
        emptyMessage="Empty :("
        columnResizeMode="onChange"
        rows={data}
        columnVisibility={columnVisibility}
        columnPinState={{
          right: ['navigate'],
        }}
        scrollbarHorizontal
        stickyHeader
        headerStyle={headerStyle}
        width="100%"
        columns={[
          {
            accessorKey: 'analogueModelId',
            enableColumnFilter: false,
            enableSorting: false,
            header: function () {
              <Styled.List>{<Checkbox></Checkbox>}</Styled.List>;
            },
            id: 'expand',
            size: 80,
            cell: ({ row }) => (
              <>
                {
                  <Checkbox
                    checked={
                      exportModels.indexOf(row.original.analogueModelId) > -1
                    }
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      addModelToExport(
                        e.target.checked,
                        row.original.analogueModelId,
                      )
                    }
                  ></Checkbox>
                }
              </>
            ),
          },
          {
            accessorKey: 'name',
            header: 'Model name',
            id: 'name',
            size: columnSizes.name,
          },
          {
            header: 'Template',
            id: 'template',
            size: columnSizes.template,
            accessorFn: (row) =>
              parseModelConfiguration(row.iniParameters).template,
          },
          {
            header: 'Basin slope',
            id: 'basinSlope',
            size: columnSizes.basinSlope,
            accessorFn: (row) =>
              parseModelConfiguration(row.iniParameters).basinSlope,
          },
          {
            header: 'Sediment classes',
            id: 'composition',
            size: columnSizes.composition,
            accessorFn: (row) =>
              parseModelConfiguration(row.iniParameters).composition,
          },
          {
            header: 'River length',
            id: 'riverLength',
            size: columnSizes.riverLength,
            accessorFn: (row) =>
              parseModelConfiguration(row.iniParameters).riverLength,
          },
          {
            header: 'Simulation time',
            id: 'simStopTime',
            size: columnSizes.simStopTime,
            accessorFn: (row) =>
              parseModelConfiguration(row.iniParameters).simStopTime,
          },
          {
            header: 'Channel width',
            id: 'channelWidth',
            size: columnSizes.channelWidth,
            accessorFn: (row) =>
              parseModelConfiguration(row.iniParameters).channelWidth,
          },
          {
            header: 'Subsidence at seaward',
            id: 'subsidenceSea',
            size: columnSizes.subsidenceSea,
            accessorFn: (row) =>
              parseModelConfiguration(row.iniParameters).subsidenceSea,
          },
          {
            header: 'Wave direction',
            id: 'waveDirection',
            size: columnSizes.waveDirection,
            accessorFn: (row) =>
              parseModelConfiguration(row.iniParameters).waveDirection,
          },
          {
            header: 'Final wave height',
            id: 'waveHeightFin',
            size: columnSizes.waveHeightFin,
            accessorFn: (row) =>
              parseModelConfiguration(row.iniParameters).waveHeightFin,
          },
          {
            header: 'Initial wave height',
            id: 'waveHeightIni',
            size: columnSizes.waveHeightIni,
            accessorFn: (row) =>
              parseModelConfiguration(row.iniParameters).waveHeightIni,
          },
          {
            header: 'Output interval',
            id: 'outputInterval',
            size: columnSizes.outputInterval,
            accessorFn: (row) =>
              parseModelConfiguration(row.iniParameters).outputInterval,
          },
          {
            header: 'Subsidence at landward',
            id: 'subsidenceLand',
            size: columnSizes.subsidenceLand,
            accessorFn: (row) =>
              parseModelConfiguration(row.iniParameters).subsidenceLand,
          },
          {
            header: 'Tidal amplitude',
            id: 'tidalAmplitude',
            size: columnSizes.tidalAmplitude,
            accessorFn: (row) =>
              parseModelConfiguration(row.iniParameters).tidalAmplitude,
          },
          {
            header: 'Final river discharge',
            id: 'riverDischargeFin',
            size: columnSizes.riverDischargeFin,
            accessorFn: (row) =>
              parseModelConfiguration(row.iniParameters).riverDischargeFin,
          },
          {
            header: 'Initial river discharge',
            id: 'riverDischargeIni',
            size: columnSizes.riverDischargeIni,
            accessorFn: (row) =>
              parseModelConfiguration(row.iniParameters).riverDischargeIni,
          },
          {
            id: 'outcrops',
            header: (header) =>
              headerComponent(header, 'Outcrop', outcropFilterList),
            filterFn: (row, columnId, filterValue) =>
              filterFunction(
                filterValue,
                row.original.outcrops.map((i) => i.name),
              ),
            size: columnSizes.outcrops,
            cell: ({ row }) => (
              <ExpandableListCell
                values={row.original.outcrops.map((a) => a.name)}
              />
            ),
          },
          {
            id: 'country',
            header: (header) =>
              headerComponent(header, 'Country', countryFilterList),
            filterFn: (row, columnId, filterValue) =>
              filterFunction(
                filterValue,
                row.original.stratigraphicGroups.map(
                  (i) => i.country.identifier,
                ),
              ),
            size: columnSizes.country,
            cell: ({ row }) => (
              <ExpandableListCell
                values={row.original.stratigraphicGroups.map(
                  (i) => i.country.identifier,
                )}
              />
            ),
          },
          {
            id: 'field',
            header: (header) =>
              headerComponent(header, 'Field', fieldFilterList),
            filterFn: (row, columnId, filterValue) =>
              filterFunction(
                filterValue,
                row.original.stratigraphicGroups.map((i) => i.field.identifier),
              ),
            size: columnSizes.field,
            cell: ({ row }) => (
              <ExpandableListCell
                values={row.original.stratigraphicGroups.map(
                  (i) => i.field.identifier,
                )}
              />
            ),
          },
          {
            id: 'stratigraphicColumn',
            header: (header) =>
              headerComponent(
                header,
                'Stratigraphic column',
                stratColFilterList,
              ),
            filterFn: (row, columnId, filterValue) =>
              filterFunction(
                filterValue,
                row.original.stratigraphicGroups.map(
                  (i) => i.stratColumn.identifier,
                ),
              ),
            size: columnSizes.stratigraphicColumn,
            cell: ({ row }) => (
              <ExpandableListCell
                values={row.original.stratigraphicGroups.map(
                  (i) => i.stratColumn.identifier,
                )}
              />
            ),
          },
          {
            id: 'group',
            header: (header) =>
              headerComponent(header, 'Group', groupFilterList),
            filterFn: (row, columnId, filterValue) =>
              filterFunction(
                filterValue,
                getRowGroup(row.original.stratigraphicGroups).map(
                  (i) => i.identifier,
                ),
              ),
            size: columnSizes.group,
            cell: ({ row }) => (
              <ExpandableListCell
                values={getRowGroup(row.original.stratigraphicGroups).map(
                  (i) => i.identifier,
                )}
              />
            ),
          },
          {
            id: 'formation',
            header: (header) =>
              headerComponent(header, 'Formation', formationFilterList),
            filterFn: (row, columnId, filterValue) =>
              filterFunction(
                filterValue,
                getRowStratUnits(row.original.stratigraphicGroups, 2).map(
                  (i) => i.identifier,
                ),
              ),
            size: columnSizes.formation,
            cell: ({ row }) => (
              <ExpandableListCell
                values={getRowStratUnits(
                  row.original.stratigraphicGroups,
                  2,
                ).map((i) => i.identifier)}
              />
            ),
          },
          {
            id: 'subzone',
            header: (header) =>
              headerComponent(header, 'Subzone', subzoneFilterList),
            filterFn: (row, columnId, filterValue) =>
              filterFunction(
                filterValue,
                getRowStratUnits(row.original.stratigraphicGroups, 3).map(
                  (i) => i.identifier,
                ),
              ),
            size: columnSizes.subzone,
            cell: ({ row }) => (
              <ExpandableListCell
                values={getRowStratUnits(
                  row.original.stratigraphicGroups,
                  3,
                ).map((i) => i.identifier)}
              />
            ),
          },
          {
            id: 'gde',
            header: (header) => headerComponent(header, 'GDE', gdeFilterList),
            filterFn: (row, columnId, filterValue) =>
              filterFunction(
                filterValue,
                row.original.geologicalGroups.map(
                  (g) => g.grossDepEnv.identifier,
                ),
              ),
            size: columnSizes.gde,
            cell: ({ row }) => (
              <ExpandableListCell
                values={row.original.geologicalGroups.map(
                  (g) => g.grossDepEnv.identifier,
                )}
              />
            ),
          },
          {
            id: 'de',
            header: (header) => headerComponent(header, 'DE', deFilterList),
            filterFn: (row, columnId, filterValue) =>
              filterFunction(
                filterValue,
                row.original.geologicalGroups.map((g) => g.depEnv.identifier),
              ),
            size: columnSizes.de,
            cell: ({ row }) => (
              <ExpandableListCell
                values={row.original.geologicalGroups.map(
                  (g) => g.depEnv.identifier,
                )}
              />
            ),
          },
          {
            id: 'se',
            header: (header) => headerComponent(header, 'SE', seFilterList),
            filterFn: (row, columnId, filterValue) =>
              filterFunction(
                filterValue,
                row.original.geologicalGroups.map((g) => g.subenv.identifier),
              ),
            size: columnSizes.se,
            cell: ({ row }) => (
              <ExpandableListCell
                values={row.original.geologicalGroups.map(
                  (g) => g.subenv.identifier,
                )}
              />
            ),
          },
          {
            id: 'ae',
            header: (header) => headerComponent(header, 'AE', aeFilterList),
            filterFn: (row, columnId, filterValue) =>
              filterFunction(filterValue, getRowArchels(row.original)),
            size: 150,
            cell: ({ row }) => (
              <ExpandableListCell values={getRowArchels(row.original)} />
            ),
          },
          {
            id: 'createdBy',
            header: (header) =>
              headerComponent(header, 'Created by', createdByFilterList),
            filterFn: (row, columnId, filterValue) =>
              filterFunction(
                filterValue,
                row.original.createdBy ? [row.original.createdBy] : [],
              ),
            size: columnSizes.createdBy,
            cell: ({ row }) => <>{row.original.createdBy ?? '-'}</>,
          },
          {
            accessorKey: 'createdDate',
            id: 'createdDate',
            header: 'Created date',
            enableColumnFilter: false,
            size: columnSizes.createdDate,
            cell: ({ row }) => (
              <>
                {row.original.createdDate
                  ? new Date(row.original.createdDate).toLocaleDateString()
                  : '-'}
              </>
            ),
          },
          {
            accessorKey: 'isProcessed',
            id: 'isProcessed',
            header: 'Status',
            enableColumnFilter: false,
            size: columnSizes.isProcessed,
            cell: ({ row }) => (
              <>{getModelStatus(row.original.analogueModelId)}</>
            ),
          },

          {
            header: 'Actions',
            id: 'navigate',
            enableColumnFilter: false,
            enableResizing: false,
            maxSize: 100,
            cell: ({ row }) => (
              <Styled.Buttons>
                <Button
                  variant="outlined"
                  onClick={() => {
                    navigate(`/models/${row.original.analogueModelId}`);
                  }}
                >
                  Open
                </Button>
              </Styled.Buttons>
            ),
          },
        ]}
      />

      <Styled.StyledSideSheet
        open={isSideSheetOpen}
        onClose={() => setIsSideSheetOpen(false)}
        title="Toggle Columns"
      >
        <Styled.SideSheetContent>
          <Styled.SideSheetTitle variant="overline">
            Columns
          </Styled.SideSheetTitle>
          <EdsProvider density="compact">
            <Styled.CheckboxColumn>
              <Checkbox
                label="Model name"
                checked={columnVisibility.name}
                onChange={() => toggleColumn('name')}
              />
              <Checkbox label="Template" checked={true} disabled />
              <Checkbox
                label="Basin slope"
                checked={columnVisibility.basinSlope}
                onChange={() => toggleColumn('basinSlope')}
              />
              <Checkbox
                label="Sediment classes"
                checked={columnVisibility.composition}
                onChange={() => toggleColumn('composition')}
              />
              <Checkbox
                label="River length"
                checked={columnVisibility.riverLength}
                onChange={() => toggleColumn('riverLength')}
              />
              <Checkbox
                label="Simulation time"
                checked={columnVisibility.simStopTime}
                onChange={() => toggleColumn('simStopTime')}
              />
              <Checkbox
                label="Channel width"
                checked={columnVisibility.channelWidth}
                onChange={() => toggleColumn('channelWidth')}
              />
              <Checkbox
                label="Subsidence at seaward"
                checked={columnVisibility.subsidenceSea}
                onChange={() => toggleColumn('subsidenceSea')}
              />
              <Checkbox
                label="Wave direction"
                checked={columnVisibility.waveDirection}
                onChange={() => toggleColumn('waveDirection')}
              />
              <Checkbox
                label="Final wave height"
                checked={columnVisibility.waveHeightFin}
                onChange={() => toggleColumn('waveHeightFin')}
              />
              <Checkbox
                label="Initial wave height"
                checked={columnVisibility.waveHeightIni}
                onChange={() => toggleColumn('waveHeightIni')}
              />
              <Checkbox
                label="Output interval"
                checked={columnVisibility.outputInterval}
                onChange={() => toggleColumn('outputInterval')}
              />
              <Checkbox
                label="Subsidence at landward"
                checked={columnVisibility.subsidenceLand}
                onChange={() => toggleColumn('subsidenceLand')}
              />
              <Checkbox
                label="Tidal amplitude"
                checked={columnVisibility.tidalAmplitude}
                onChange={() => toggleColumn('tidalAmplitude')}
              />
              <Checkbox
                label="Final river discharge"
                checked={columnVisibility.riverDischargeFin}
                onChange={() => toggleColumn('riverDischargeFin')}
              />
              <Checkbox
                label="Initial river discharge"
                checked={columnVisibility.riverDischargeIni}
                onChange={() => toggleColumn('riverDischargeIni')}
              />
              <Checkbox
                label="Outcrop"
                checked={columnVisibility.outcrops}
                onChange={() => toggleColumn('outcrops')}
              />
              <Checkbox
                label="Country"
                checked={columnVisibility.country}
                onChange={() => toggleColumn('country')}
              />
              <Checkbox
                label="Field"
                checked={columnVisibility.field}
                onChange={() => toggleColumn('field')}
              />
              <Checkbox
                label="Stratigraphic column"
                checked={columnVisibility.stratigraphicColumn}
                onChange={() => toggleColumn('stratigraphicColumn')}
              />
              <Checkbox
                label="Group"
                checked={columnVisibility.group}
                onChange={() => toggleColumn('group')}
              />
              <Checkbox
                label="Formation"
                checked={columnVisibility.formation}
                onChange={() => toggleColumn('formation')}
              />
              <Checkbox
                label="Subzone"
                checked={columnVisibility.subzone}
                onChange={() => toggleColumn('subzone')}
              />
              <Checkbox
                label="GDE"
                checked={columnVisibility.gde}
                onChange={() => toggleColumn('gde')}
              />
              <Checkbox
                label="DE"
                checked={columnVisibility.de}
                onChange={() => toggleColumn('de')}
              />
              <Checkbox
                label="SE"
                checked={columnVisibility.se}
                onChange={() => toggleColumn('se')}
              />
              <Checkbox
                label="AE"
                checked={columnVisibility.ae}
                onChange={() => toggleColumn('ae')}
              />
              <Checkbox
                label="Status"
                checked={columnVisibility.isProcessed}
                onChange={() => toggleColumn('isProcessed')}
              />
              <Checkbox
                label="Created by"
                checked={columnVisibility.createdBy}
                onChange={() => toggleColumn('createdBy')}
              />
              <Checkbox
                label="Created date"
                checked={columnVisibility.createdDate}
                onChange={() => toggleColumn('createdDate')}
              />
            </Styled.CheckboxColumn>
          </EdsProvider>
        </Styled.SideSheetContent>
      </Styled.StyledSideSheet>
    </Styled.Table>
  );
};
